import {
  ChakraProvider,
  ChakraTheme,
  theme as chakraTheme,
  useTheme as useChakraTheme,
} from '@chakra-ui/react'
import * as React from 'react'

import { ThemeProviderRaw } from '~theme/ThemeProvider'
import { TTheme } from '~theme/typings'
import { buildTheme } from '~theme/utils'

/**
 * Base Theme type. Use or extend if you want to use UI module components with another theme provider.
 */
export type TThemeBase = TTheme

type TProps = RequiredChildren & FirstParam<typeof buildTheme>

/**
 * Theme provider for (not only) Chakra components. Accepts company theme styles & merges them with
 * preset defaults.
 */
export const UIThemeProvider: React.FC<TProps> = ({ children, ...themeColors }) => {
  // chakra & emotion need its own providers – provide the same data as the basic react provider from theme package
  // this is necessary eg. for theme variable inside `styled` templates
  const theme = buildTheme(themeColors)

  // extend our theme with part of the chakra-ui theme, so its components work properly
  // provide it via the ChakraProvider
  const extendedChakraTheme: Partial<ChakraTheme> = React.useMemo(() => {
    const componentsTheme = {
      ...chakraTheme.components,

      // turn off lower opacity for disabled buttons
      Button: {
        ...chakraTheme.components.Button,
        baseStyle: {
          _disabled: {
            opacity: 1,
          },
        },
      },

      // turn off chakra styles for radio buttons
      Radio: {
        baseStyle: {},
      },

      // turn off chakra styles for popovers
      Popover: {
        baseStyle: {},
      },

      // turn off chakra styles for menu
      Menu: {
        ...chakraTheme.components.Menu,
        baseStyle: {},
      },
    }

    return {
      ...theme,
      components: componentsTheme,
      transition: chakraTheme.transition,
    }
  }, [theme])

  return (
    <ThemeProviderRaw value={theme}>
      <ChakraProvider theme={extendedChakraTheme}>{children}</ChakraProvider>
    </ThemeProviderRaw>
  )
}

/**
 * If you need to use the theme provided via context above in a component, this is your guy.
 */
export const useTheme = () => {
  return useChakraTheme<TThemeBase>()
}
